/**
 * Reactive view over the optimistic-turn store (`lib/optimisticMessages.ts`)
 * reconciled against the real message stream for one conversation.
 *
 * Also does the housekeeping:
 * - once a turn's reply has actually landed in Firestore, it's dropped from
 *   the store — from that point the real `messages` array is the single
 *   source of truth for it;
 * - once an open turn has been waiting past `THINKING_TIMEOUT_MS`, it's
 *   flipped to 'error' (see `findTimedOutTurns`) so it stops disabling the
 *   composer and gains the retry/discard affordance instead of leaving the
 *   thinking indicator stuck forever.
 */
import type { MessageDoc } from '@agente/shared'
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react'

import {
  type ClassifiedTurn,
  classifyPendingTurns,
  findTimedOutTurns,
  getPendingTurns,
  type PendingTurn,
  removePendingTurn,
  setPendingTurnError,
  subscribePendingTurns,
  THINKING_TIMEOUT_MESSAGE,
} from '@/lib/optimisticMessages'

/** A pending turn plus render-only context derived from reconciliation. */
export interface RenderableTurn extends PendingTurn {
  /** Only set for errored turns: a real message matching it already landed. */
  likelySent: boolean
}

export interface OptimisticTurnsResult {
  /** Optimistic user bubbles not yet mirrored by a real Firestore doc (submit order). */
  pendingUserTurns: RenderableTurn[]
  /** Show the "thinking" placeholder in place of the next assistant reply. */
  showThinking: boolean
}

function isOpen({ phase }: ClassifiedTurn): boolean {
  return phase === 'sending' || phase === 'awaiting-reply'
}

export function useOptimisticTurns(key: string, messages: MessageDoc[]): OptimisticTurnsResult {
  const turns = useSyncExternalStore(
    (listener) => subscribePendingTurns(key, listener),
    () => getPendingTurns(key),
    () => getPendingTurns(key),
  )

  const classified = useMemo(() => classifyPendingTurns(messages, turns), [messages, turns])

  // Always-fresh view of `classified` for the timeout interval below, which
  // must keep checking on its own even while nothing else re-renders this hook.
  const classifiedRef = useRef(classified)
  classifiedRef.current = classified

  // Drop turns whose reply has actually landed — immediate, no polling needed.
  useEffect(() => {
    for (const { turn, phase } of classified) {
      if (phase === 'resolved') removePendingTurn(key, turn.localId)
    }
  }, [classified, key])

  const hasOpenTurns = classified.some(isOpen)

  // Defensive timeout: while any turn is open, poll every few seconds and
  // flip anything past THINKING_TIMEOUT_MS to 'error'. Re-armed whenever the
  // classification changes (new turn, a turn resolving/erroring, etc.); while
  // nothing changes — the exact "stuck" scenario this guards against — the
  // same interval keeps running against the live ref.
  useEffect(() => {
    if (!hasOpenTurns) return
    const check = () => {
      for (const localId of findTimedOutTurns(classifiedRef.current, Date.now())) {
        setPendingTurnError(key, localId, THINKING_TIMEOUT_MESSAGE)
      }
    }
    check()
    const id = setInterval(check, 5_000)
    return () => clearInterval(id)
  }, [hasOpenTurns, key])

  return {
    pendingUserTurns: classified
      .filter(({ phase }) => phase === 'sending' || phase === 'error')
      .map(({ turn, likelySent }) => ({ ...turn, likelySent })),
    showThinking: hasOpenTurns,
  }
}
